import { z } from 'zod'

const appEnvironmentSchema = z.enum(['development', 'staging', 'production', 'test'])

const rawEnvironmentSchema = z.object({
  VITE_API_BASE_URL: z.string().trim().default(''),
  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive().max(120_000).default(15_000),
  VITE_APP_ENV: appEnvironmentSchema.default('development'),
  VITE_GOOGLE_MAPS_KEY: z.string().trim().default(''),
  VITE_SITE_URL: z.string().trim().default(''),
})

export interface AppEnvironment {
  apiBaseUrl: string
  apiTimeoutMs: number
  appEnvironment: z.infer<typeof appEnvironmentSchema>
  googleMapsKey: string
  siteUrl: string
}

function normalizeBaseUrl(value: string) {
  return value.replace(/\/+$/, '')
}

export function parseEnvironment(source: Record<string, unknown>): AppEnvironment {
  const parsed = rawEnvironmentSchema.parse(source)
  const apiBaseUrl = normalizeBaseUrl(parsed.VITE_API_BASE_URL)
  const siteUrl = normalizeBaseUrl(parsed.VITE_SITE_URL)

  if (parsed.VITE_APP_ENV === 'production' && /localhost|127\.0\.0\.1/i.test(apiBaseUrl)) {
    throw new Error('Production VITE_API_BASE_URL must not use localhost.')
  }
  if (parsed.VITE_APP_ENV === 'production' && siteUrl && !siteUrl.startsWith('https://')) {
    throw new Error('VITE_SITE_URL must use HTTPS in production when configured.')
  }

  return {
    apiBaseUrl,
    apiTimeoutMs: parsed.VITE_API_TIMEOUT_MS,
    appEnvironment: parsed.VITE_APP_ENV,
    googleMapsKey: parsed.VITE_GOOGLE_MAPS_KEY,
    siteUrl,
  }
}

export const env = parseEnvironment({
  ...import.meta.env,
  VITE_APP_ENV: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
})
